/**
 * SERP PWA lifecycle
 * - Registers /sw.js at load time (production-safe: no-op in dev if URL absent).
 * - Detects when a new worker is `installed` and *waiting*, then fires a
 *   sonner toast ("New version available") with a Reload action that tells
 *   the waiting SW to skipWaiting and reloads the page.
 * - Also handles browser controllerchange (works after skipWaiting).
 */
import { toast } from "sonner";

let _reloading = false;

function promptReload(registration) {
  toast("New version available", {
    id: "serp-sw-update",
    description: "A fresh build of SERP is ready. Reload to update.",
    duration: Infinity,
    action: {
      label: "Reload",
      onClick: () => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        } else {
          window.location.reload();
        }
      },
    },
    cancel: { label: "Later", onClick: () => toast.dismiss("serp-sw-update") },
  });
}

function watchWaiting(registration) {
  if (!registration) return;
  if (registration.waiting) {
    promptReload(registration);
    return;
  }
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) {
        promptReload(registration);
      }
    });
  });
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register on HTTPS or localhost (browsers reject SW on plain HTTP).
  const isSecure = window.isSecureContext || window.location.hostname === "localhost";
  if (!isSecure) return;

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      watchWaiting(registration);

      // Track the very first controller for this page so we can distinguish
      // an initial install (no controller → controller) from a subsequent
      // activation-after-update (controller → new controller). Only the
      // latter deserves the "You're on the latest build" toast.
      const hadControllerAtLoad = !!navigator.serviceWorker.controller;
      let deployToastShown = false;

      const showDeployToast = () => {
        if (deployToastShown) return;
        deployToastShown = true;
        // Read the current build tag from the active shell cache name.
        (async () => {
          let buildTag = "";
          try {
            const names = await caches.keys();
            const shell = names.find((n) => n.startsWith("serp-shell-"));
            if (shell) buildTag = shell.replace("serp-shell-", "");
          } catch (_e) { /* ignore */ }
          toast.success("You're on the latest build", {
            id: "serp-deploy-marker",
            description: buildTag ? `Build ${buildTag}` : undefined,
            duration: 3000,
          });
        })();
      };

      // If controller changes (i.e. skipWaiting has taken over), reload once.
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (hadControllerAtLoad) {
          // Genuine post-update activation → surface a deploy marker toast.
          showDeployToast();
        }
        if (_reloading) return;
        _reloading = true;
        // Delay the reload slightly so the toast has time to be seen.
        setTimeout(() => window.location.reload(), 800);
      });

      // Poll for updates every 60s (light-weight — browser cache is used).
      setInterval(() => { registration.update().catch(() => {}); }, 60_000);
    } catch (err) {
      console.warn("[SERP PWA] SW registration failed:", err);
    }
  });
}
